import mapboxgl from 'mapbox-gl';
import { MAPBOX_TOKEN, MOROCCO_CENTERS, createMapboxTransformRequest } from '../config/mapboxConfig';
import { ensureMapboxRtlTextPlugin } from './mapboxRtl';
import logger from '@/utils/logger';

/**
 * Gestionnaire d'instance Mapbox partagée (pattern reuseMap).
 *
 * Pourquoi : sur Mapbox GL JS, le SKU facturé est le « map load » — une session
 * par `new mapboxgl.Map()`. Recréer une carte à chaque navigation entre pages
 * cartographiques (Tracking, Fleet, History, Superadmin) génère un map load
 * facturé à chaque fois. Toutes ces cartes utilisent le même style
 * `streets-v12`, donc une instance unique réutilisée suffit.
 *
 * Principe :
 *  - une seule `mapboxgl.Map` vit tout au long de la session admin ;
 *  - son conteneur DOM est déplacé dans la page qui l'`acquire()` ;
 *  - au `release()`, on purge les couches/sources/marqueurs ajoutés par la page
 *    puis on parque le conteneur dans un holder caché (la carte n'est pas détruite).
 *
 * Les composants restent responsables de retirer leurs propres marqueurs (ils en
 * gardent les refs). Le `release()` fait un nettoyage générique de sécurité :
 * suppression de toute couche/source absente du snapshot du style de base, et
 * balayage des marqueurs DOM résiduels.
 */

const BASE_STYLE = 'mapbox://styles/mapbox/streets-v12';

let _map = null;
let _container = null; // div dans lequel Mapbox rend (déplacé entre pages)
let _holder = null; // div caché qui parque le conteneur au repos
let _ready = false;
let _baseLayerIds = null; // snapshot couches du style de base (pour purge)
let _baseSourceIds = null; // snapshot sources du style de base (pour purge)
let _readyCbs = [];
let _fullscreenControl = null; // FullscreenControl propre à l'acquisition courante
let _resizeObserver = null; // observe le conteneur → resize() quand il (re)devient visible
let _owner = null; // consommateur actif (détient le conteneur)
// owner -> { parentEl, options, cleanups: fn[], viewApplied: bool }
let _consumers = new Map();

function ensureHolder() {
  if (_holder) return _holder;
  _holder = document.createElement('div');
  // Hors écran MAIS rendu (surtout pas display:none) : garde le contexte WebGL
  // de la carte au repos et lui conserve une taille → évite la carte blanche au
  // retour de navigation (un canvas Mapbox en display:none peut perdre son
  // contexte GL et ne jamais repeindre).
  _holder.style.position = 'fixed';
  _holder.style.left = '-10000px';
  _holder.style.top = '0';
  _holder.style.width = '800px';
  _holder.style.height = '600px';
  _holder.style.overflow = 'hidden';
  _holder.style.opacity = '0';
  _holder.style.pointerEvents = 'none';
  _holder.setAttribute('data-shared-map-holder', '');
  document.body.appendChild(_holder);
  return _holder;
}

function snapshotBaseStyle() {
  try {
    const style = _map.getStyle();
    _baseLayerIds = new Set((style?.layers || []).map((l) => l.id));
    _baseSourceIds = new Set(Object.keys(style?.sources || {}));
  } catch {
    /* style pas encore prêt — re-snapshot au prochain ready */
  }
}

function markReady() {
  if (!_baseLayerIds) snapshotBaseStyle();
  if (_ready) return;
  _ready = true;
  const cbs = _readyCbs;
  _readyCbs = [];
  cbs.forEach((cb) => {
    try {
      cb(_map);
    } catch (e) {
      logger.warn('⚠️ [sharedMap] onReady callback error:', e);
    }
  });
}

function createMap(parentEl) {
  ensureHolder();

  _container = document.createElement('div');
  _container.style.width = '100%';
  _container.style.height = '100%';
  parentEl.appendChild(_container);

  mapboxgl.accessToken = MAPBOX_TOKEN;
  ensureMapboxRtlTextPlugin();

  _map = new mapboxgl.Map({
    container: _container,
    style: BASE_STYLE,
    center: [MOROCCO_CENTERS.casablanca.lng, MOROCCO_CENTERS.casablanca.lat],
    zoom: 12,
    transformRequest: createMapboxTransformRequest(),
    // Cohérent avec l'ensemble des cartes du projet (logo/attribution masqués via
    // classes CSS sur les wrappers, ou attributionControl:false à l'origine).
    attributionControl: false,
  });

  // NavigationControl reste permanent (identique pour tous les consommateurs).
  // FullscreenControl est géré par acquisition car sa cible plein écran varie
  // selon la page (conteneur carte seul, ou wrapper carte + panneau).
  _map.addControl(new mapboxgl.NavigationControl(), 'top-right');

  _map.on('load', markReady);
  _map.on('idle', () => {
    if (_map?.loaded()) markReady();
  });

  // Le conteneur est déplacé entre pages (et parqué dans un holder caché au repos).
  // Quand il revient dans un parent visible et dimensionné, sa taille passe de 0×0
  // à WxH → on déclenche resize() pour que Mapbox repeigne. Sans cela, la carte
  // réutilisée reste blanche après une navigation. (rAF seul est trop précoce.)
  if (typeof ResizeObserver !== 'undefined') {
    _resizeObserver = new ResizeObserver(() => {
      try {
        _map?.resize();
      } catch {
        /* ignore */
      }
    });
    _resizeObserver.observe(_container);
  }

  logger.debug('🗺️ [sharedMap] Instance Mapbox créée (1 map load)');
}

function applyView(view = {}) {
  if (!_map) return;
  try {
    if (view.bounds) {
      _map.fitBounds(view.bounds, {
        ...(view.fitBoundsOptions || {}),
        animate: false,
      });
    } else if (view.center) {
      _map.jumpTo({ center: view.center, zoom: view.zoom ?? _map.getZoom() });
    }
  } catch (e) {
    logger.warn('⚠️ [sharedMap] applyView error:', e);
  }
}

/**
 * Acquiert l'instance partagée : la crée si besoin, déplace son conteneur dans
 * `parentEl`, applique la vue initiale, (ré)installe le contrôle plein écran et
 * redimensionne.
 * @param {HTMLElement} parentEl conteneur de la page hôte
 * @param {object} options
 * @param {[number, number]} [options.center]
 * @param {number} [options.zoom]
 * @param {any} [options.bounds]
 * @param {any} [options.fitBoundsOptions]
 * @param {HTMLElement} [options.fullscreenContainer] élément cible du plein écran
 *        (défaut : le conteneur de la carte)
 * @param {object} [owner] jeton unique du consommateur. Permet à un `release()`
 *        tardif (ordre cleanup/setup React, StrictMode) de devenir un no-op une
 *        fois qu'un autre consommateur a acquis la carte → évite la carte blanche.
 * @returns {mapboxgl.Map}
 */
export function acquireSharedMap(parentEl, options = {}, owner = null) {
  if (!parentEl) return null;
  const key = owner || {}; // jeton d'identité (auto si non fourni — usage hérité/tests)

  if (!_map) {
    createMap(parentEl);
  }

  // Un autre consommateur détenait la carte : on retire SES listeners (évite les
  // doublons) mais SANS purger les couches/sources. Un survivant de la même page
  // les réutilise ; la purge générique n'a lieu qu'au parking réel (release sans
  // survivant), ce qui couvre les navigations inter-pages (cleanup-before-setup).
  if (_owner && _owner !== key) {
    runOwnerCleanups(_owner);
  }

  if (!_consumers.has(key)) {
    _consumers.set(key, {
      parentEl,
      options,
      cleanups: [],
      viewApplied: false,
    });
  }
  activateConsumer(key);

  return _map;
}

/** Rend `owner` actif : déplace le conteneur dans sa page, (ré)installe le plein écran, recadre. */
function activateConsumer(owner) {
  const entry = _consumers.get(owner);
  if (!entry || !_map) return;
  _owner = owner;
  if (_container && _container.parentElement !== entry.parentEl) {
    entry.parentEl.appendChild(_container);
  }
  installFullscreenControl(entry.options.fullscreenContainer);
  // Vue appliquée une seule fois par consommateur : au handover (réactivation),
  // on préserve le cadrage courant (pan/zoom utilisateur) au lieu de le réinitialiser.
  if (!entry.viewApplied) {
    applyView(entry.options);
    entry.viewApplied = true;
  }
  scheduleResize();
}

function parkContainer() {
  ensureHolder();
  if (_container && _container.parentElement !== _holder) {
    _holder.appendChild(_container);
  }
}

/**
 * Force plusieurs resize() après le (re)montage : le conteneur peut n'avoir sa
 * taille finale qu'après le layout de la nouvelle page. Un seul rAF est trop
 * précoce → on échelonne, plus un resize au prochain 'idle'. Le ResizeObserver
 * couvre les changements de taille ultérieurs.
 */
function scheduleResize() {
  const doResize = () => {
    try {
      _map?.resize();
    } catch {
      /* ignore */
    }
  };
  requestAnimationFrame(doResize);
  setTimeout(doResize, 60);
  setTimeout(doResize, 200);
  try {
    _map?.once('idle', doResize);
  } catch {
    /* ignore */
  }
}

function installFullscreenControl(fullscreenContainer) {
  if (!_map) return;
  // Retirer le contrôle de l'acquisition précédente (cible potentiellement différente)
  if (_fullscreenControl) {
    try {
      _map.removeControl(_fullscreenControl);
    } catch {
      /* ignore */
    }
    _fullscreenControl = null;
  }
  try {
    _fullscreenControl = fullscreenContainer
      ? new mapboxgl.FullscreenControl({ container: fullscreenContainer })
      : new mapboxgl.FullscreenControl();
    _map.addControl(_fullscreenControl, 'top-right');
  } catch (e) {
    logger.warn('⚠️ [sharedMap] FullscreenControl:', e);
  }
}

/**
 * Enregistre un callback de nettoyage **rattaché au consommateur actif**, exécuté
 * quand celui-ci est relâché ou supplanté. Les consommateurs l'utilisent pour
 * retirer leurs propres listeners
 * (ex: `map.on('click', fn)` → `registerMapCleanup(() => map.off('click', fn))`).
 */
export function registerMapCleanup(fn) {
  if (typeof fn !== 'function') return;
  const entry = _consumers.get(_owner);
  if (entry) entry.cleanups.push(fn);
}

/**
 * Exécute `cb(map)` quand le style est chargé (immédiatement si déjà prêt).
 */
export function onSharedMapReady(cb) {
  if (typeof cb !== 'function') return;
  if (_ready && _map) {
    cb(_map);
    return;
  }
  _readyCbs.push(cb);
}

export function isSharedMapReady() {
  return _ready && !!_map;
}

export function getSharedMap() {
  return _map;
}

/** Exécute (LIFO) puis vide les cleanups d'un consommateur — ses propres listeners. */
function runOwnerCleanups(owner) {
  const entry = _consumers.get(owner);
  if (!entry || entry.cleanups.length === 0) return;
  const cbs = entry.cleanups;
  entry.cleanups = [];
  for (let i = cbs.length - 1; i >= 0; i--) {
    try {
      cbs[i]();
    } catch (e) {
      logger.warn('⚠️ [sharedMap] cleanup consommateur:', e);
    }
  }
}

/** Retire le contrôle plein écran de l'acquisition courante. */
function removeFullscreenControl() {
  if (_fullscreenControl) {
    try {
      _map.removeControl(_fullscreenControl);
    } catch {
      /* ignore */
    }
    _fullscreenControl = null;
  }
}

/**
 * Purge générique : couches/sources hors style de base + marqueurs/popups DOM
 * résiduels. Appelée UNIQUEMENT au parking (plus aucun consommateur) — un
 * handover/supersede de la même page réutilise ces couches, il ne faut donc pas
 * les supprimer dans ces cas. Les navigations inter-pages parquent (cleanup-
 * before-setup) → la purge y a bien lieu.
 *
 * Si le style n'est pas encore chargé, `getStyle()` lève « Style is not done
 * loading » : on saute la purge (aucune couche custom n'a pu être ajoutée).
 */
function purgeMapAdditions() {
  if (!_map) return;

  const styleReady = !_map.isStyleLoaded || _map.isStyleLoaded();
  if (styleReady) {
    try {
      const style = _map.getStyle();
      if (_baseLayerIds) {
        (style?.layers || []).forEach((l) => {
          if (!_baseLayerIds.has(l.id)) {
            try {
              _map.removeLayer(l.id);
            } catch {
              /* ignore */
            }
          }
        });
      }
      if (_baseSourceIds) {
        Object.keys(style?.sources || {}).forEach((id) => {
          if (!_baseSourceIds.has(id)) {
            try {
              _map.removeSource(id);
            } catch {
              /* ignore */
            }
          }
        });
      }
    } catch (e) {
      logger.warn('⚠️ [sharedMap] purge couches/sources:', e);
    }
  }

  try {
    _container?.querySelectorAll('.mapboxgl-marker').forEach((el) => el.remove());
    _container?.querySelectorAll('.mapboxgl-popup').forEach((el) => el.remove());
  } catch {
    /* ignore */
  }
}

/**
 * Relâche l'instance pour le consommateur `owner` : exécute ses cleanups puis,
 * s'il était actif, confie la carte à un consommateur encore monté (handover,
 * sans purge) ou — s'il n'en reste aucun — purge les ajouts et parque le conteneur.
 *
 * Si `owner` ne correspond pas au détenteur courant (un autre consommateur a déjà
 * acquis la carte entre-temps), l'appel ne fait que retirer ses listeners et son
 * entrée : c'est ce qui empêche un `release()` tardif de re-parquer une carte
 * qu'une autre page vient d'acquérir (cause de la carte blanche au retour de nav).
 */
export function releaseSharedMap(owner = null) {
  if (!_map) return;

  // Appel hérité sans jeton (certains tests) : tout nettoyer + parquer.
  if (!owner) {
    _consumers.forEach((_entry, key) => runOwnerCleanups(key));
    _consumers.clear();
    removeFullscreenControl();
    purgeMapAdditions();
    parkContainer();
    _owner = null;
    return;
  }

  runOwnerCleanups(owner);
  const wasActive = _owner === owner;
  _consumers.delete(owner);

  // Consommateur supplanté → on s'est contenté de retirer ses listeners/entrée.
  if (!wasActive) return;

  _owner = null;
  if (_consumers.size > 0) {
    // Handover vers un consommateur encore monté (même page) : PAS de purge,
    // il réutilise les couches/marqueurs déjà en place.
    const next = [..._consumers.keys()][_consumers.size - 1];
    activateConsumer(next);
  } else {
    // Plus aucun consommateur : nettoyage réel + parking.
    removeFullscreenControl();
    purgeMapAdditions();
    parkContainer();
  }
}

/**
 * Détruit complètement l'instance (logout, tests). Rarement nécessaire en prod.
 */
export function destroySharedMap() {
  try {
    _resizeObserver?.disconnect();
  } catch {
    /* ignore */
  }
  _resizeObserver = null;
  try {
    _map?.remove();
  } catch {
    /* ignore */
  }
  _map = null;
  _container = null;
  _ready = false;
  _baseLayerIds = null;
  _baseSourceIds = null;
  _readyCbs = [];
  _fullscreenControl = null;
  _owner = null;
  _consumers = new Map();
}
